import type {
  Chain,
  ChainPlugin,
  VerifyInput,
  VerifyOptions,
  VerifyResult,
  Warning,
} from './types.js';
import { constantTimeEqual } from './security/constant-time.js';
import { hexToBytes, bytesToHex } from './utils/hex.js';
import {
  UnsupportedChainError,
  ValidationFailedError,
} from './errors.js';

/**
 * Resolve the plugin for a given chain from the registered plugins.
 */
function resolvePlugin(
  chain: Chain,
  plugins: readonly ChainPlugin[],
): ChainPlugin {
  const plugin = plugins.find((p) => p.chain === chain);
  if (!plugin) {
    throw new UnsupportedChainError(chain);
  }
  return plugin;
}

/**
 * Verify a transaction hash and decode it into human-readable form.
 *
 * This is the main entry point for the library. It:
 * 1. Resolves the chain plugin
 * 2. Validates the transaction input
 * 3. Serializes the transaction to wire format
 * 4. Hashes the serialized data
 * 5. Compares the computed hash to the expected hash (constant-time)
 * 6. Decodes the transaction (only if hash matches)
 * 7. Returns the verification result
 *
 * @param input - The transaction data and expected hash
 * @param options - Registered chain plugins
 * @returns Verification result with decoded transaction (if verified)
 *
 * @throws {UnsupportedChainError} If no plugin for the chain
 * @throws {ValidationFailedError} If the transaction input is invalid
 * @throws {SerializationError} If serialization fails unexpectedly
 */
export async function verify(
  input: VerifyInput,
  options: VerifyOptions,
): Promise<VerifyResult> {
  const warnings: Warning[] = [];

  // Step 1: Resolve plugin
  const plugin = resolvePlugin(input.chain, options.plugins);

  // Step 2: Determine the chain-specific input to pass to the plugin
  const chainInput = getChainInput(input);

  // Step 3: Validate input
  const validation = plugin.validateInput(chainInput);
  if (!validation.valid) {
    throw new ValidationFailedError(input.chain, validation.errors);
  }
  warnings.push(...validation.warnings);

  // Step 4: Serialize to wire format
  const serialized = plugin.serialize(chainInput);

  // Step 5: Hash the serialized data
  // Await in case the plugin returns a Promise (e.g., Bitcoin uses async Web Crypto SHA-256)
  const computedHashBytes = await plugin.hash(serialized);
  const computedHash = bytesToHex(computedHashBytes);

  // Step 6: Constant-time comparison
  // Normalize expected hash to bytes for comparison
  const expectedHashBytes = hexToBytes(input.expectedHash);
  const verified = constantTimeEqual(computedHashBytes, expectedHashBytes);

  // Step 7: Decode the transaction
  // Always decode so users can see what the transaction claims to do.
  // When verification fails, the decoded data is marked as unverified
  // and a critical warning is added so the UI can distinguish the two cases.
  let decoded = null;
  try {
    decoded = await plugin.decode(chainInput);
  } catch {
    // Decoding failed — not critical, we still return the verification result
  }

  if (!verified) {
    warnings.push({
      message:
        'Hash mismatch: the transaction data does NOT match the hash you are being asked to sign. DO NOT sign this transaction.',
      severity: 'critical',
    });
  }

  return {
    verified,
    computedHash,
    expectedHash: input.expectedHash,
    decoded,
    chain: input.chain,
    warnings,
  };
}

/**
 * Extract the chain-specific input from the unified VerifyInput.
 */
function getChainInput(input: VerifyInput): unknown {
  switch (input.chain) {
    case 'ethereum': {
      if ('typedData' in input) {
        return input.typedData;
      }
      return {
        transaction: input.transaction,
        abi: 'abi' in input ? input.abi : undefined,
      };
    }
    case 'bitcoin':
      return input.transaction;
    case 'solana':
      return input.transaction;
  }
}
